import React, { useEffect, useState } from 'react';
import { RefreshCw, Inbox, Phone, Mail, MapPin, Loader2, Calendar } from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { adminListEstimates, adminUpdateEstimate } from '../../lib/api';
import { LEAD_STATUSES } from '../../mock';
import { useToast } from '../../hooks/use-toast';

const STATUS_STYLES = {
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-amber-100 text-amber-700',
  estimate_sent: 'bg-purple-100 text-purple-700',
  won: 'bg-green-100 text-green-700',
  lost: 'bg-red-100 text-red-700',
};

const LeadsPanel = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      setItems(await adminListEstimates());
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const changeStatus = async (id, status) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status } : it)));
    try {
      await adminUpdateEstimate(id, status);
      toast({ title: 'Lead updated' });
    } catch (e) {
      toast({ title: 'Update failed', variant: 'destructive' });
      load();
    }
  };

  const filtered = filter === 'all' ? items : items.filter((i) => i.status === filter);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold text-[#123020]">Estimate Requests</h1>
          <p className="text-[#5a6b60] mt-1 text-[14px]">{items.length} total lead{items.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-10 w-[170px] bg-white border-[#d7d5c8]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {LEAD_STATUSES.map((s) => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
            </SelectContent>
          </Select>
          <Button onClick={load} variant="outline" className="border-[#5f9e2c] text-[#5f9e2c] hover:bg-[#5f9e2c] hover:text-white bg-white"><RefreshCw className="mr-2 h-4 w-4" /> Refresh</Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-[#5a6b60]"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-[#5a6b60]"><Inbox className="h-12 w-12 mb-3 text-[#a9b7a0]" /><p>No estimate requests found.</p></div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {filtered.map((it) => (
            <div key={it.id} className="bg-white rounded-md p-6 shadow-sm ring-1 ring-[#e6e4d7]">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-lg text-[#123020]">{it.full_name}</h3>
                  <span className="inline-block mt-1 text-[12px] font-medium px-2 py-0.5 rounded bg-[#eef5e6] text-[#5f9e2c]">{it.service}</span>
                </div>
                <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${STATUS_STYLES[it.status] || ''}`}>
                  {(LEAD_STATUSES.find((s) => s.value === it.status) || {}).label || it.status}
                </span>
              </div>
              <div className="mt-4 space-y-2 text-[14px] text-[#41524a]">
                <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-[#5f9e2c]" /> <a href={`tel:${it.phone}`} className="hover:underline">{it.phone}</a></div>
                <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-[#5f9e2c]" /> <a href={`mailto:${it.email}`} className="hover:underline break-all">{it.email}</a></div>
                <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#5f9e2c]" /> {it.location}</div>
                <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-[#5f9e2c]" /> {new Date(it.created_at).toLocaleString()}</div>
              </div>
              {it.details && <p className="mt-4 pt-3 border-t border-[#eceadd] text-[14px] text-[#5a6b60]">{it.details}</p>}
              <div className="mt-4 pt-3 border-t border-[#eceadd]">
                <label className="text-[12px] font-medium text-[#8a9980] block mb-1.5">Update status</label>
                <Select value={it.status} onValueChange={(v) => changeStatus(it.id, v)}>
                  <SelectTrigger className="h-10 bg-white border-[#d7d5c8]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LEAD_STATUSES.map((s) => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LeadsPanel;
